import Microgame from '../engine/Microgame.js';

export default class TankGame extends Microgame {

    static getPlayback() {
        return {
            title: { fr: `Tank !`, en: `Tank!`, es: `¡Tanque!` },
            controls: { fr: `Clique pour tirer, déplace la cible pour orienter le canon`, en: `Click to shoot, move the target to aim`, es: `Haz clic para disparar, mueve el objetivo para apuntar` },
            objective: { fr: `Détruis les tourelles ennemies avant qu'elles ne te touchent`, en: `Destroy enemy turrets before they hit you`, es: `Destruye las torretas enemigas antes de que te golpeen` },
            icon: `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="var(--primary-color)" stroke-width="2"><rect x="3" y="11" width="18" height="6" rx="2" ry="2"/><path d="M12 11V7h5"/><circle cx="7" cy="11" r="2"/><circle cx="17" cy="11" r="2"/><path d="m5 17 1 4h12l1-4"/></svg>`,
            actions: [
                { type: 'wait', duration: 260 },
                { type: 'move', x: 50, y: 78, duration: 420 },
                { type: 'wait', duration: 2200 },
            ],
            loopDuration: 3200
        };
    }
    constructor(engine) {
        super(engine);
        this.player = null;
        this.enemy = null;
        this.bullets = [];
        this.playerX = this.isPlayback ? 50 : 10 + Math.random() * 80;
        this.playerY = this.isPlayback ? 78 : 70 + Math.random() * 20;
        
        // Ensure enemy doesn't spawn exactly in front (minimum 20% distance)
        let ex;
        if (this.isPlayback) {
            ex = 50;
        } else {
            do {
                ex = 10 + Math.random() * 80;
            } while (Math.abs(ex - this.playerX) < 20);
        }
        
        this.enemyX = ex;
        this.enemyY = this.isPlayback ? 18 : 10 + Math.random() * 20;
        this.shootTimer = 0;
        // Random first shot delay (between 250ms and 750ms)
        this.enemyShootTimer = this.isPlayback ? 1200 : 250 + Math.random() * 500; 
        
        // Difficulty scaling: 3 seconds at x1, 1.3 seconds at x4
        this.speedMult = engine.speedMultiplier || 1.0;
        
        // Firing frequency: Cap at the "old speed 3" level (which was 2.0)
        // Damping even more as requested (0.3 instead of 0.5)
        this.dampedMult = Math.min(1 + (this.speedMult - 1) * 0.33, 2.0);
        
        // Calculation for timer: 1x -> 3000ms (timer 3s), 4x -> 5200ms (timer 1.3s)
        this.duration = 3000 + (this.speedMult - 1) * (2200 / 3);
    }

    setup() {
        this.engine.ui.instruction.textContent = "DESTROY!";

        // Player
        this.player = this.createTank(this.primaryColor, 'up');
        this.player.style.top = `${this.playerY}%`;
        this.player.style.left = `${this.playerX}%`;
        this.container.appendChild(this.player);

        // Enemy (Immobile)
        this.enemy = this.createTank(this.secondaryColor, 'down');
        this.enemy.style.top = `${this.enemyY}%`;
        this.enemy.style.left = `${this.enemyX}%`;
        this.container.appendChild(this.enemy);

        // Border Line (Middle)
        const line = document.createElement('div');
        line.style.position = 'absolute';
        line.style.width = '100%';
        line.style.height = '2px';
        line.style.background = this.secondaryColor;
        line.style.top = '50%';
        line.style.opacity = '0.3';
        line.style.boxShadow = `0 0 10px ${this.secondaryColor}`;
        this.container.appendChild(line);

        this.onMove = this.onMove.bind(this);
        this.engine.input.element.addEventListener('pointermove', this.onMove);
    }

    createTank(color, dir) {
        const tankContainer = document.createElement('div');
        tankContainer.style.position = 'absolute';
        tankContainer.style.width = '44px';
        tankContainer.style.height = '44px';
        tankContainer.style.transform = 'translate(-50%, -50%)';

        // Treads (Chenilles)
        const treadLeft = document.createElement('div');
        const treadRight = document.createElement('div');
        [treadLeft, treadRight].forEach((t, i) => {
            t.style.position = 'absolute';
            t.style.width = '10px';
            t.style.height = '100%';
            t.style.background = '#333';
            t.style.border = `1px solid ${color}`;
            t.style.left = i === 0 ? '0' : '34px';
            t.style.borderRadius = '3px';
            t.style.boxShadow = `0 0 5px ${color}`;
            // Simple animated pattern for treads
            t.style.backgroundImage = `linear-gradient(transparent 50%, rgba(255,255,255,0.1) 50%)`;
            t.style.backgroundSize = '100% 4px';
            tankContainer.appendChild(t);
        });

        // Main Body
        const tank = document.createElement('div');
        tank.style.position = 'absolute';
        tank.style.width = '24px';
        tank.style.height = '36px';
        tank.style.background = color;
        tank.style.left = '10px';
        tank.style.top = '4px';
        tank.style.borderRadius = '4px';
        tank.style.boxShadow = `0 0 10px ${color}`;
        tankContainer.appendChild(tank);

        // Turret
        const turret = document.createElement('div');
        turret.style.position = 'absolute';
        turret.style.width = '8px';
        turret.style.height = '28px';
        turret.style.background = '#fff';
        turret.style.left = '18px';
        turret.style.boxShadow = `0 0 8px #fff`;
        if (dir === 'up') {
            turret.style.top = '-10px';
            turret.style.borderRadius = '2px 2px 0 0';
        } else {
            turret.style.bottom = '-10px';
            turret.style.borderRadius = '0 0 2px 2px';
        }
        tankContainer.appendChild(turret);

        return tankContainer;
    }

    update(dt) {
        if (!this.active || this.won) return;

        // Enemy Shoot (Reduced cadence, capped at 3.4)
        this.enemyShootTimer -= dt;
        if (this.enemyShootTimer <= 0) {
            this.spawnBullet(this.enemyX, this.enemyY, 1, 'enemy');
            this.enemyShootTimer = (1500 + Math.random() * 1000) / this.dampedMult;
        }

        // Player Shoot (Increased cadence, capped at 3.4)
        this.shootTimer -= dt;
        if (this.shootTimer <= 0) {
            this.spawnBullet(this.playerX, this.playerY, -1, 'player');
            this.shootTimer = 300 / this.dampedMult;
        }

        // Bullets
        for (let i = this.bullets.length - 1; i >= 0; i--) {
            const b = this.bullets[i];
            b.y += b.vy * this.speedMult * (dt / 1000);
            b.el.style.top = `${b.y}%`;

            // Hit Enemy
            if (b.type === 'player' && Math.abs(b.y - this.enemyY) < 6 && Math.abs(b.x - this.enemyX) < 6) {
                this.engine.timer = Infinity; // Stop timer immediately!
                this.active = false; // Stop further updates but keep container alive
                this.explode(this.enemyX, this.enemyY, this.secondaryColor);
                this.enemy.style.opacity = '0';
                b.el.remove();
                this.bullets.splice(i, 1);
                
                // Finalize after animation
                setTimeout(() => {
                    this.active = true; // Temporary restore for win() check
                    this.win();
                }, 700);
                return;
            }

            // Hit Player
            if (b.type === 'enemy' && Math.abs(b.y - this.playerY) < 6 && Math.abs(b.x - this.playerX) < 6) {
                this.engine.timer = Infinity; // Stop timer
                this.active = false;
                this.explode(this.playerX, this.playerY, this.primaryColor);
                this.player.style.opacity = '0';
                b.el.remove();
                this.bullets.splice(i, 1);
                
                setTimeout(() => {
                    this.active = true;
                    this.lose();
                }, 700);
                return;
            }

            if (b.y < 0 || b.y > 100) {
                b.el.remove();
                this.bullets.splice(i, 1);
            }
        }
    }

    explode(x, y, color) {
        for (let i = 0; i < 15; i++) {
            const part = document.createElement('div');
            part.style.position = 'absolute';
            part.style.width = '10px';
            part.style.height = '10px';
            part.style.background = color;
            part.style.left = `${x}%`;
            part.style.top = `${y}%`;
            part.style.borderRadius = '50%';
            part.style.boxShadow = `0 0 15px ${color}`;
            this.container.appendChild(part);

            const angle = Math.random() * Math.PI * 2;
            const dist = 30 + Math.random() * 50;
            const tx = Math.cos(angle) * dist;
            const ty = Math.sin(angle) * dist;

            part.animate([
                { transform: 'translate(-50%, -50%) scale(1)', opacity: 1 },
                { transform: `translate(calc(-50% + ${tx}px), calc(-50% + ${ty}px)) scale(0)`, opacity: 0 }
            ], {
                duration: 600,
                easing: 'ease-out',
                fill: 'forwards'
            });

            setTimeout(() => part.remove(), 600);
        }
    }

    spawnBullet(x, y, dir, type) {
        const b = document.createElement('div');
        b.style.position = 'absolute';
        b.style.width = '8px';
        b.style.height = '8px';
        b.style.background = type === 'player' ? '#fff' : '#f00';
        b.style.borderRadius = '50%';
        b.style.left = `${x}%`;
        b.style.top = `${y}%`;
        b.style.transform = 'translate(-50%, -50%)';
        b.style.boxShadow = `0 0 8px ${type === 'player' ? '#fff' : '#f00'}`;
        this.container.appendChild(b);

        this.bullets.push({ el: b, x, y, vy: dir * 80, type });
    }

    onMove(e) {
        if (!this.active || this.won) return;
        const rect = this.container.getBoundingClientRect();
        let nx = (e.clientX - rect.left) / rect.width * 100;
        let ny = (e.clientY - rect.top) / rect.height * 100;

        // Constraint: Don't cross the middle (50%)
        if (ny < 55) ny = 55; // Player must stay in the bottom half (+ buffer)
        
        this.playerX = nx;
        this.playerY = ny;

        this.player.style.left = `${this.playerX}%`;
        this.player.style.top = `${this.playerY}%`;
    }

    cleanup() {
        super.cleanup();
        this.engine.input.element.removeEventListener('pointermove', this.onMove);
    }
}
